import { useEffect, useState } from 'react'
import { Clock3Icon, RefreshCwIcon, ShieldCheckIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'

const AUTO_RETRY_SECONDS = 10

interface MaintenanceViewProps {
  onRetry: () => void
  variant?: 'maintenance' | 'unavailable'
}

export function MaintenanceView({ onRetry, variant = 'maintenance' }: MaintenanceViewProps) {
  const { t } = useTranslation()
  const [secondsUntilRetry, setSecondsUntilRetry] = useState(AUTO_RETRY_SECONDS)
  const isUnavailable = variant === 'unavailable'

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setSecondsUntilRetry((current) => {
        if (current <= 1) {
          window.clearInterval(intervalId)
          window.setTimeout(onRetry, 0)
          return 0
        }

        return current - 1
      })
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [onRetry])

  return (
    <div className="flex min-h-[100svh] flex-col overflow-x-hidden bg-[linear-gradient(180deg,#F8FBFF_0%,#FFFFFF_48%,#F6F8FB_100%)] text-slate-950">
      <main className="flex flex-1 items-center justify-center px-4 py-8 sm:py-12">
        <section className="flex w-full max-w-3xl flex-col items-center text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#2563EB]">
            {t('maintenance.badge')}
          </p>

          <h1 className="mt-3 max-w-2xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            {t(isUnavailable ? 'maintenance.unavailable_title' : 'maintenance.title')}
          </h1>

          <p className="mt-4 max-w-lg text-base leading-7 text-slate-600 sm:text-lg">
            {t(isUnavailable ? 'maintenance.unavailable_description' : 'maintenance.description')}
          </p>

          <div className="mt-6 flex w-full justify-center sm:mt-7">
            <img
              src="/maintenance-illustration.svg"
              alt=""
              className="h-auto w-full max-w-[310px] sm:max-w-[380px]"
            />
          </div>

          <div className="mt-6 flex w-full max-w-xl items-start gap-4 rounded-lg border border-slate-200 bg-white px-5 py-4 text-left shadow-[0_18px_55px_rgba(15,23,42,0.08)] sm:px-6">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[#2563EB] ring-1 ring-blue-100">
              <Clock3Icon className="h-7 w-7" />
            </span>
            <div className="min-w-0 pt-1">
              <h2 className="text-base font-semibold text-slate-950">
                {t(isUnavailable ? 'maintenance.unavailable_next_step_title' : 'maintenance.next_step_title')}
              </h2>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                {t(isUnavailable ? 'maintenance.unavailable_next_step_description' : 'maintenance.next_step_description')}
              </p>
            </div>
          </div>

          <Button
            onClick={onRetry}
            className="mt-5 h-12 min-w-56 rounded-md bg-[#2563EB] px-7 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(37,99,235,0.22)] hover:bg-[#1D4ED8]"
          >
            <RefreshCwIcon className="mr-2 h-4 w-4" />
            {t('actions.try_again')}
          </Button>

          <p className="mt-3 text-xs font-medium text-slate-500">
            {t('maintenance.retry_countdown', { seconds: secondsUntilRetry })}
          </p>
        </section>
      </main>

      <footer className="border-t border-slate-200/70 px-4 py-5">
        <div className="mx-auto flex max-w-3xl items-center justify-center gap-3 text-center text-xs leading-5 text-slate-500">
          <ShieldCheckIcon className="h-4 w-4 shrink-0 text-[#2563EB]" />
          <span>{t('maintenance.retry_hint')}</span>
        </div>
      </footer>
    </div>
  )
}
